import type { LucideIcon, LucideProps } from 'lucide-react';
import { forwardRef } from 'react';
import { cn } from './cn';
import iconStyles from './icon.module.css';

export type IconComponent = LucideIcon;
export interface IconProps extends Omit<LucideProps, 'ref'> {
  icon: IconComponent;
}

/** One glyph renderer for controls and content. Context-relative em boxes must
 * keep viewBox-scaled strokes; numeric boxes can request absolute stroke width.
 * Forwarding the real SVG ref is required by composed focus/tooltip controls. */
export const Icon = forwardRef<SVGSVGElement, IconProps>(function Icon(
  {
    icon: Glyph,
    size = '1.25em',
    strokeWidth = 2.25,
    absoluteStrokeWidth = true,
    'aria-hidden': hidden,
    'aria-label': label,
    ...props
  },
  ref
) {
  const numeric = typeof size === 'number' ? size : Number(size);
  return (
    <Glyph
      {...props}
      ref={ref}
      size={size}
      strokeWidth={strokeWidth}
      // Em boxes already scale strokes with their viewBox. Omit the Lucide-only
      // flag unless numeric sizing requests it: custom stroked SVG glyphs also
      // satisfy this contract and must not receive an unknown DOM property.
      {...(absoluteStrokeWidth && Number.isFinite(numeric)
        ? { absoluteStrokeWidth: true }
        : {})}
      aria-hidden={hidden ?? (label ? undefined : true)}
      aria-label={label}
      role={props.role ?? (label ? 'img' : undefined)}
      focusable={false}
      data-app-icon="true"
    />
  );
});

export interface SvgIconProps extends IconProps {
  fontSize?: 'small' | 'medium' | 'large' | 'inherit';
  titleAccess?: string;
}
const SEMANTIC_INK: Record<string, string> = {
  action: 'var(--minerva-palette-action-active)',
  primary: 'var(--color-primary-text)',
  secondary: 'var(--minerva-palette-secondary-main)',
  error: 'var(--color-destructive-text)',
  warning: 'var(--minerva-palette-warning-main)',
  info: 'var(--minerva-palette-info-main)',
  success: 'var(--minerva-palette-success-main)',
  disabled: 'var(--minerva-palette-action-disabled)',
  inherit: 'inherit',
};

/** Preset content glyph boxes preserve the former 20/24/35px rem geometry,
 * without another SVG wrapper, theme hooks, emotion, or a style interpreter.
 * Controls still own their slot square through the canonical button recipe. */
export const SvgIcon = forwardRef<SVGSVGElement, SvgIconProps>(function SvgIcon(
  { fontSize = 'medium', titleAccess, size, color, style, className, ...props },
  ref
) {
  return (
    <Icon
      {...props}
      ref={ref}
      size={size ?? '1em'}
      className={cn(iconStyles.glyph, iconStyles[fontSize], className)}
      color={color ? (SEMANTIC_INK[color] ?? color) : undefined}
      aria-label={props['aria-label'] ?? titleAccess}
      style={{ fill: 'none', ...style }}
    />
  );
});
